import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Rocket, RefreshCw, ExternalLink, FileCode2, ArrowLeft, CheckCircle2,
  XCircle, Download, Loader2, Clock, Pencil, Info, TriangleAlert, PlugZap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useSite } from "@/context/SiteContext";
import { withSession } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Log line icons for the dark console (brand palette on dark).
const LEVEL_ICON = {
  success: <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-[#7fb2ff]" />,
  error: <XCircle size={13} className="mt-0.5 shrink-0 text-[#ffab7a]" />,
  warn: <TriangleAlert size={13} className="mt-0.5 shrink-0 text-amber-400" />,
  info: <Info size={13} className="mt-0.5 shrink-0 text-white/40" />,
};

const StatusBadge = ({ ok, children, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-[8px] border px-2.5 py-1 font-nunito text-[10px] font-extrabold uppercase tracking-[0.1em] ${
      ok ? "border-[#c8dcf7] bg-[#e9f1fd] text-[#0050c0]" : "border-[#f8ddc0] bg-[#fef2e5] text-[#c25a00]"
    }`}
  >
    {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
    {children}
  </span>
);

/**
 * Live Deploy Engine - always operates on the ACTIVE workspace from the top
 * bar dropdown. Seed workspace (Slingshot) uses the compiled target registry;
 * every other workspace uses its database page registry and its own stored
 * WordPress credentials. Workspaces without a WordPress connection get a
 * single call-to-action that opens THE one WordPress connector (the workspace
 * settings sheet) - connection forms are never duplicated on other screens.
 */
export default function DeployEnginePage() {
  const { activeSite, setSettingsOpen, fetchHealth } = useSite();
  const siteId = activeSite?.id;
  const isSeed = !!activeSite?.is_seed;
  const wpConnected = !!activeSite?.wp_connected;

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(null);
  const [history, setHistory] = useState(null);
  const pollRef = useRef(null);
  const logRef = useRef(null);

  const fetchStatus = useCallback(async (id) => {
    if (!id) return;
    setStatusLoading(true);
    try {
      const { data } = await axios.get(`${API}/wp/status`, { params: { site_id: id } });
      setStatus(data);
      setTarget((cur) => {
        const keys = (data.targets || []).map((t) => t.key);
        return cur && keys.includes(cur) ? cur : keys[0] || null;
      });
    } catch (e) {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/wp/deployments`, { params: { limit: 6, site_id: id } });
      setHistory(data);
    } catch (e) {
      setHistory([]);
    }
  }, []);

  // Everything on this page follows the workspace dropdown.
  useEffect(() => {
    clearInterval(pollRef.current);
    setJob(null);
    setStarting(null);
    setStatus(null);
    setHistory(null);
    if (siteId && wpConnected) {
      fetchStatus(siteId);
      fetchHistory(siteId);
    } else {
      setStatusLoading(false);
    }
    return () => clearInterval(pollRef.current);
  }, [siteId, wpConnected, fetchStatus, fetchHistory]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [job?.steps?.length]);

  const startDeploy = async (mode) => {
    setStarting(mode);
    setJob(null);
    try {
      const { data } = await axios.post(`${API}/wp/deploy`, {
        target, mode, confirm: mode === "live", site_id: siteId,
      });
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API}/wp/deploy/${data.job_id}`);
          setJob(j);
          if (j.status === "completed" || j.status === "failed") {
            clearInterval(pollRef.current);
            setStarting(null);
            fetchHistory(siteId);
            fetchHealth(siteId, true);
            if (j.status === "completed") toast.success(`Deployment completed (${mode}).`);
            else toast.error("Deployment failed — see the log for details.");
          }
        } catch (err) {
          /* keep polling */
        }
      }, 1800);
    } catch (e) {
      setStarting(null);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the deployment.");
    }
  };

  const running = !!starting && (!job || (job.status !== "completed" && job.status !== "failed"));
  const targets = status?.targets || [];
  const selected = targets.find((t) => t.key === target);

  return (
    <div data-testid="deploy-page" className="min-h-screen bg-[#fafafa]">
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#c8dcf7] bg-[#e9f1fd] px-2.5 py-1 font-nunito text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#0050c0]">
              <Rocket size={12} /> Live Deploy Engine
            </span>
            <h1 className="mt-3 font-nunito text-[26px] font-extrabold leading-tight text-[#020202]">
              Deploy <span className="text-[#0050c0]" data-testid="deploy-active-site-name">{activeSite?.name || "…"}</span> to WordPress
            </h1>
            <p className="mt-1.5 max-w-xl font-poppins text-[13.5px] leading-relaxed text-[#6e6e6e]">
              Pushes the selected mockup page — markup, media and Flatsome shortcodes — straight into{" "}
              <span className="font-semibold text-[#383838]">{activeSite?.production_domain || "the connected WordPress site"}</span>{" "}
              over the REST API. Draft first, go live when it looks right.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isSeed && (
              <Link
                to="/flatsome-export"
                data-testid="deploy-export-link"
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-black/15 px-3 py-2 font-nunito text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#383838] transition-colors duration-150 hover:border-[#0050c0]/40 hover:text-[#0050c0]"
              >
                <FileCode2 size={13} /> Export Spec
              </Link>
            )}
            <Link
              to="/"
              data-testid="deploy-back-link"
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-black/15 px-3 py-2 font-nunito text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#383838] transition-colors duration-150 hover:border-[#0050c0]/40 hover:text-[#0050c0]"
            >
              <ArrowLeft size={13} /> Back
            </Link>
          </div>
        </div>

        {/* Not connected -> the ONE WordPress connector */}
        {activeSite && !wpConnected ? (
          <div data-testid="deploy-connect-gate" className="mt-10 rounded-[14px] border border-[#f8ddc0] bg-[#fef2e5]/50 p-10 text-center">
            <PlugZap size={34} className="mx-auto text-[#f07000]" />
            <h2 className="mt-4 font-nunito text-[19px] font-extrabold text-[#383838]">WordPress connection required</h2>
            <p className="mx-auto mt-2 max-w-md font-poppins text-[13.5px] leading-relaxed text-[#6e6e6e]">
              You need to be connected to WordPress for these features to work. Add{" "}
              <span className="font-semibold text-[#383838]">{activeSite.name}</span>&rsquo;s site URL and an
              application password once — every deploy tool unlocks automatically.
            </p>
            <Button
              type="button"
              data-testid="deploy-connect-wordpress-button"
              onClick={() => setSettingsOpen(true)}
              className="mt-5 rounded-[10px] bg-[#0050c0] px-5 font-nunito text-xs font-extrabold uppercase tracking-wide text-white hover:bg-[#0343a0]"
            >
              <PlugZap size={13} /> Connect WordPress Now
            </Button>
          </div>
        ) : (
          <>
            {/* Connection status */}
            <div data-testid="deploy-status-card" className="mt-8 rounded-[14px] bg-white border border-[#ececec] shadow-[0_6px_18px_rgba(2,2,2,0.04)] p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Connection Status</h2>
                <button
                  type="button"
                  data-testid="deploy-status-refresh-button"
                  onClick={() => fetchStatus(siteId)}
                  disabled={statusLoading}
                  className="inline-flex items-center gap-1.5 font-nunito text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#6e6e6e] transition-colors duration-150 hover:text-[#0050c0] disabled:opacity-50"
                >
                  <RefreshCw size={12} className={statusLoading ? "animate-spin" : ""} /> Refresh
                </button>
              </div>
              {statusLoading && !status ? (
                <div className="mt-5 flex flex-wrap gap-2.5">
                  <Skeleton className="h-7 w-40 rounded-[8px]" />
                  <Skeleton className="h-7 w-32 rounded-[8px]" />
                  <Skeleton className="h-7 w-36 rounded-[8px]" />
                </div>
              ) : (
                <div className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="deploy-connection-badges">
                  <StatusBadge ok={!!status?.configured} testId="deploy-badge-configured">
                    {status?.configured ? "Credentials configured" : "Not configured"}
                  </StatusBadge>
                  <StatusBadge ok={!!status?.connected} testId="deploy-badge-connected">
                    {status?.connected ? `Connected as ${status?.user_name || "?"}` : "Not connected"}
                  </StatusBadge>
                  {status?.connected && (
                    <>
                      <StatusBadge ok={!!status?.is_admin} testId="deploy-badge-admin">
                        {status?.is_admin ? "Administrator" : "Limited role"}
                      </StatusBadge>
                      <StatusBadge ok={!!status?.unfiltered_html} testId="deploy-badge-html">
                        {status?.unfiltered_html ? "Unfiltered HTML" : "HTML filtered"}
                      </StatusBadge>
                    </>
                  )}
                  {status?.site && (
                    <a href={status.site} target="_blank" rel="noopener noreferrer" className="font-mono text-[12px] text-[#0050c0] hover:underline">
                      {status.site}
                    </a>
                  )}
                  {status && !status.connected && status.error && (
                    <p className="basis-full font-poppins text-[12px] text-[#c25a00]">{status.error}</p>
                  )}
                </div>
              )}
            </div>

            {/* Deploy card */}
            <div className="mt-6 rounded-[14px] bg-white border border-[#ececec] shadow-[0_6px_18px_rgba(2,2,2,0.04)] p-6">
              <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Choose a page target</h2>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="deploy-target-picker">
                {targets.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`deploy-target-${t.key}`}
                    onClick={() => setTarget(t.key)}
                    disabled={running}
                    className={`rounded-[10px] border px-3 py-2 text-left transition-colors duration-150 ${
                      target === t.key
                        ? "border-[#0050c0] bg-[#e9f1fd]"
                        : "border-black/10 bg-white hover:border-[#0050c0]/40"
                    }`}
                  >
                    <span className={`block font-nunito text-[11px] font-extrabold uppercase tracking-[0.06em] ${target === t.key ? "text-[#0050c0]" : "text-[#383838]"}`}>
                      {t.label}
                    </span>
                    <span className="block max-w-[220px] truncate font-poppins text-[10.5px] text-[#8a8a8a]">{t.description}</span>
                  </button>
                ))}
                {!targets.length && !statusLoading && (
                  <p className="font-poppins text-[12.5px] text-[#8a8a8a]">
                    No pages in this workspace yet — add or import pages in the{" "}
                    <Link to="/workspace" className="font-bold text-[#0050c0] hover:underline">Workspace</Link> first.
                  </p>
                )}
              </div>

              <ul className="mt-5 space-y-1.5">
                {[
                  "Uploads the page media and brand icon set to the WP media library (skips duplicates)",
                  "Creates or updates the page with the full Flatsome-compatible markup",
                  "Draft mode never touches the live page — a preview link comes back instead",
                ].map((line, i) => (
                  <li key={i} className="flex items-start gap-2 font-poppins text-[12.5px] text-[#6e6e6e]">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#0050c0]" />
                    {line}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <Button
                  type="button"
                  data-testid="deploy-start-button"
                  disabled={running || !target || !status?.connected}
                  onClick={() => startDeploy("draft")}
                  className="rounded-[10px] bg-[#f07000] font-nunito text-xs font-extrabold uppercase tracking-wide text-white hover:bg-[#d96400]"
                >
                  {starting === "draft" && running ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
                  Deploy Draft
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      data-testid="deploy-live-button"
                      disabled={running || !target || !status?.connected}
                      className="rounded-[10px] bg-[#0050c0] font-nunito text-xs font-extrabold uppercase tracking-wide text-white hover:bg-[#0343a0]"
                    >
                      {starting === "live" && running ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                      Deploy Live
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="deploy-live-confirm-dialog" className="rounded-[14px]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-nunito font-extrabold">Overwrite the live page?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This immediately replaces /{selected?.slug || "?"}/ on {activeSite?.production_domain || status?.site} with the mockup
                        version. WordPress keeps a revision, and every deploy here can be rolled back from the history below.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="deploy-live-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="deploy-live-confirm-yes"
                        onClick={() => startDeploy("live")}
                        className="bg-[#0050c0] text-white hover:bg-[#0343a0]"
                      >
                        Yes, deploy live
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Live log */}
              {job && (
                <div className="mt-6">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#6e6e6e]">Deployment log</h3>
                    <span
                      className={`font-nunito text-[11px] font-extrabold uppercase tracking-[0.1em] ${
                        job.status === "completed" ? "text-[#0050c0]" : job.status === "failed" ? "text-[#c25a00]" : "text-[#b45309]"
                      }`}
                      data-testid="deploy-job-status"
                    >
                      {job.status}
                    </span>
                  </div>
                  <div ref={logRef} data-testid="deploy-log" className="rounded-[12px] bg-[#0b0b0c] p-4 max-h-[300px] overflow-auto space-y-2">
                    {(job.steps || []).map((s, i) => (
                      <div key={i} className="flex items-start gap-2 font-mono text-[11.5px] leading-snug text-white/80">
                        {LEVEL_ICON[s.level] || LEVEL_ICON.info}
                        <span>{s.message}</span>
                      </div>
                    ))}
                    {running && (
                      <div className="flex items-center gap-2 font-mono text-[11.5px] text-white/40">
                        <Loader2 size={12} className="animate-spin" /> working…
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Result banner */}
              {job?.status === "completed" && job.result && (
                <div data-testid="deploy-result" className="mt-5 rounded-[12px] border border-[#c8dcf7] bg-[#e9f1fd] p-5">
                  <p className="font-nunito text-[13px] font-extrabold text-[#0050c0]">
                    Deployment complete — {job.result.media_uploaded ?? 0} media items, page ID {job.result.page_id ?? "?"}.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {job.result.live_link && (
                      <Button asChild size="sm" data-testid="deploy-result-live-link" className="rounded-[8px] bg-[#0050c0] font-nunito text-[10px] font-extrabold uppercase tracking-wide text-white hover:bg-[#0343a0]">
                        <a href={job.result.live_link} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /> View live page</a>
                      </Button>
                    )}
                    {job.result.preview_link && (
                      <Button asChild size="sm" variant="outline" data-testid="deploy-result-preview-link" className="rounded-[8px] border-[#0050c0]/40 font-nunito text-[10px] font-extrabold uppercase tracking-wide text-[#0050c0] hover:bg-[#e9f1fd]">
                        <a href={job.result.preview_link} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /> Preview draft</a>
                      </Button>
                    )}
                    {job.result.edit_link && (
                      <Button asChild size="sm" variant="outline" data-testid="deploy-result-edit-link" className="rounded-[8px] border-black/20 font-nunito text-[10px] font-extrabold uppercase tracking-wide text-[#383838] hover:bg-black/[0.04]">
                        <a href={job.result.edit_link} target="_blank" rel="noopener noreferrer"><Pencil size={12} /> Edit in WP</a>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Icon kit (seed-only: compiled Slingshot brand assets) */}
            {isSeed && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-[14px] bg-white border border-[#ececec] shadow-[0_6px_18px_rgba(2,2,2,0.04)] p-6">
                <div>
                  <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Brand icon kit</h2>
                  <p className="mt-1 font-poppins text-[12.5px] text-[#6e6e6e]">
                    The compiled PNG icon set the deploy engine uploads — download it for manual WP work.
                  </p>
                </div>
                <Button asChild variant="outline" data-testid="icon-kit-download-button" className="rounded-[10px] border-black/20 font-nunito text-[11px] font-extrabold uppercase tracking-wide text-[#383838] hover:bg-black/[0.04]">
                  <a href={withSession(`${API}/icons/kit.zip`)}><Download size={13} /> Download kit.zip</a>
                </Button>
              </div>
            )}

            {/* History */}
            <div className="mt-6 rounded-[14px] bg-white border border-[#ececec] shadow-[0_6px_18px_rgba(2,2,2,0.04)] p-6" data-testid="deploy-history">
              <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Recent deployments</h2>
              {history === null ? (
                <div className="mt-4 space-y-2">
                  <Skeleton className="h-9 rounded-[8px]" />
                  <Skeleton className="h-9 rounded-[8px]" />
                </div>
              ) : history.length === 0 ? (
                <p className="mt-3 font-poppins text-[12.5px] text-[#8a8a8a]">No deployments yet for this workspace.</p>
              ) : (
                <div className="mt-3 divide-y divide-black/[0.06]">
                  {history.map((h) => (
                    <div key={h.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                      <span className={`inline-flex items-center rounded-[6px] border px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] ${
                        h.mode === "live" ? "border-[#c8dcf7] bg-[#e9f1fd] text-[#0050c0]" : h.mode === "rollback" ? "border-[#f0e3ae] bg-[#fdf7e1] text-[#8a6d00]" : "border-[#f8ddc0] bg-[#fef2e5] text-[#c25a00]"
                      }`}>
                        {h.mode}
                      </span>
                      <span className="font-nunito text-[11.5px] font-extrabold text-[#383838]">{h.target_label || h.target || "?"}</span>
                      <span className={`font-nunito text-[10px] font-extrabold uppercase tracking-[0.08em] ${
                        h.status === "completed" ? "text-[#0050c0]" : h.status === "failed" ? "text-[#c25a00]" : "text-[#b45309]"
                      }`}>
                        {h.status}
                      </span>
                      <span className="ml-auto inline-flex items-center gap-1 font-poppins text-[11px] text-[#8a8a8a]">
                        <Clock size={11} /> {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
